/**
 * Les MOVES — ce qu'on sort de sa trousse pour avancer.
 *
 * Deux familles, qui ne se ressemblent pas :
 *
 *  • un **Move-savoir** est une connaissance : une formule, un théorème. Il n'agit pas sur l'écriture,
 *    il en APPORTE une (« A = b × h ÷ 2 »). On le débloque en reconnaissant la bonne écriture parmi
 *    les erreurs classiques — donc l'énoncé ne donne jamais la formule, sinon il n'y a rien à trouver.
 *
 *  • un **Move-geste** est un acte pratique sur l'écriture qu'on a sous les yeux : remplacer une lettre
 *    par sa valeur, développer, factoriser, passer un terme de l'autre côté du signe égal. Il ne dit
 *    rien de nouveau, il transforme. On le débloque en reconnaissant la règle, puis on l'applique.
 *
 * Où vit la vérité : la LIGNE d'un Move-savoir est celle que la feuille accepte déjà — le `pred` de
 * `scratch_models` (`{label, line, why, hora: 3}`), lui-même issu d'une `card_rules` de la SKILLCARD.
 * Ce catalogue ne l'invente pas : il la recopie et lui ajoute ce qui manquait, les DISTRACTEURS.
 * Quand les Moves passeront en base (table `moves`), c'est ce couple règle + distracteurs qui y ira.
 */

import { sheetToolLines } from '@jeraisonne/brouillon-engine/sheet-ui';
import type { SchoolLevel } from '@jeraisonne/db';
import { schoolLevelRank } from '@jeraisonne/db/cards';

export type MoveKind = 'savoir' | 'geste';

/**
 * Les familles de touches du clavier qu'un Move-geste ouvre. Chacune a SA rangée (`ToolRow.kind`) ou, pour
 * la commutativité, ses flèches ← → et le glissé d'un terme : un geste non pris n'en montre aucune.
 */
export type MoveGesture =
  | 'commute'
  | 'substitute'
  | 'distribute'
  | 'develop'
  | 'factor'
  | 'cross'
  | 'crossmul';

/**
 * Le TYPE GÉNÉRIQUE d'un Move (`docs/brouillon/MOVES_INVENTAIRE.md` §2) : ce qui le fait marcher.
 *  - `interface` : une famille de touches codée dans le clavier (commuter, remplacer…) ;
 *  - `rewrite` : un outil « réécriture » d'un pack (développer, factoriser, produit en croix) ;
 *  - `equation` : une transformation d'équation du pack (ajouter aux deux membres…) ;
 *  - `formula` : une formule que la feuille accepte (`pred`) ;
 *  - `theorem` : un théorème (même mécanisme qu'une formule, ou une règle de démonstration) ;
 *  - `value` : une table de valeurs à connaître.
 * Seul `interface` demande du code pour un nouveau Move ; les autres sont des données.
 */
export type MoveType = 'interface' | 'rewrite' | 'equation' | 'formula' | 'theorem' | 'value';
export const MOVE_TYPES: { id: MoveType; label: string; kind: MoveKind }[] = [
  { id: 'interface', label: 'Geste d’interface (touches codées)', kind: 'geste' },
  { id: 'rewrite', label: 'Réécriture du pack', kind: 'geste' },
  { id: 'equation', label: 'Transformation d’équation', kind: 'geste' },
  { id: 'formula', label: 'Formule', kind: 'savoir' },
  { id: 'theorem', label: 'Théorème', kind: 'savoir' },
  { id: 'value', label: 'Valeurs à connaître', kind: 'savoir' },
];

/**
 * Où les touches d'un geste se posent : sous la ligne en cours, à sa droite, dans le dock du clavier, ou à
 * la place de tout le dock (le geste occupe tout l'écran, comme l'appariement de la distributivité).
 * null : la place par défaut de sa famille.
 */
export type MovePlacement = 'below_line' | 'line_right' | 'dock' | 'full_dock';
export const MOVE_PLACEMENTS: { id: MovePlacement; label: string }[] = [
  { id: 'below_line', label: 'Juste en dessous de la ligne' },
  { id: 'line_right', label: 'À droite de la ligne' },
  { id: 'dock', label: 'Dans le dock du clavier' },
  { id: 'full_dock', label: 'À la place de tout le clavier' },
];

/** Le script qui enseigne le Move (aujourd'hui : le coach de la démo guidée, ou aucun). */
export const MOVE_SCRIPTS: { id: string; label: string }[] = [
  { id: 'demo', label: 'Démo guidée (le coach l’enseigne)' },
  { id: 'tutorial', label: 'Didacticiel de la feuille' },
];

/** Une écriture proposée au déblocage. Une seule est juste ; les autres sont de vraies erreurs. */
export interface MoveChoice {
  text: string;
  ok?: true;
  /** ce qu'on dit quand on la touche — jamais « faux » tout court */
  why: string;
}

export interface MoveDef {
  id: string;
  kind: MoveKind;
  /** le nom lu sur l'étagère de la trousse */
  name: string;
  /** la phrase du cours, montrée une fois le Move débloqué */
  why: string;
  /** savoir : la ligne posée sur la feuille (= le `pred.line` de la feuille) */
  writes?: string;
  /** geste : la famille de touches du moteur que le Move ouvre */
  gesture?: MoveGesture;
  /** les écritures du déblocage, dans l'ordre d'affichage */
  choices: MoveChoice[];
  levels: SchoolLevel[];
  /**
   * Les HORA GLOBALES gagnées au déblocage (une fois par compte) : 50 pour un geste simple, jusqu'à
   * 1 000 pour le théorème le plus costaud. Jamais comptées dans l'exercice en cours. ⚠️ Miroir de la
   * table SQL `move_catalog` (migration 0751), seule source de ce qui est crédité — parité testée.
   */
  hora: number;
  /** le type générique (ce qui le fait marcher) */
  type: MoveType;
  /** faux : le Move n'existe pas pour les élèves (jamais proposé ; ses touches ne sont pas verrouillées) */
  active: boolean;
  /** vrai : acquis d'office — ses touches sont là dès le départ, sans le prendre dans la trousse */
  unlockedByDefault: boolean;
  /** où ses touches se posent ; null = la place par défaut */
  placement: MovePlacement | null;
  /** le script qui l'enseigne (`MOVE_SCRIPTS`), ou null */
  script: string | null;
  /** les SKILLCARDS associées : le Move passe devant dans la trousse quand la question est reliée à l'une d'elles */
  cardIds: string[];
  /** l'ordre dans la trousse (à pertinence égale) */
  position: number;
  /** note interne de l'admin */
  notes: string;
}

/** Le type par défaut des Moves livrés avec l'app (miroir du seed de la migration 0752). */
const DEFAULT_TYPES: Record<string, MoveType> = {
  commuter: 'interface',
  remplacer: 'interface',
  passer: 'interface',
  distribuer: 'interface',
  developper: 'rewrite',
  factoriser: 'rewrite',
  'produit-en-croix': 'rewrite',
  pythagore: 'theorem',
  thales: 'theorem',
  'point-fixe': 'theorem',
};

let position = 0;
const extras = (id: string, kind: MoveKind) => ({
  type: DEFAULT_TYPES[id] ?? (kind === 'savoir' ? ('formula' as const) : ('interface' as const)),
  active: true,
  unlockedByDefault: false,
  placement: null,
  script: null,
  cardIds: [] as string[],
  position: (position += 10),
  notes: '',
});

/**
 * Ce que rapporte chaque Move au déblocage, en HORA globales. Un geste se paie selon ce qu'il demande
 * (commuter < remplacer < développer < factoriser) ; une connaissance selon le théorème (une aire
 * < Pythagore < Thalès < les racines du trinôme < le point fixe d'une suite).
 */
export const MOVE_HORA: Record<string, number> = {
  // gestes
  commuter: 50,
  remplacer: 50,
  passer: 100,
  distribuer: 150,
  developper: 200,
  'produit-en-croix': 200,
  factoriser: 250,
  // connaissances
  'perimetre-rectangle': 50,
  'aire-rectangle': 50,
  'aire-carre': 50,
  'aire-triangle': 100,
  'somme-angles': 100,
  'somme-probas': 150,
  determinant: 400,
  pythagore: 500,
  thales: 600,
  discriminant: 700,
  'racines-trinome': 800,
  'point-fixe': 1000,
};

const S = (
  id: string,
  name: string,
  why: string,
  writes: string,
  choices: MoveChoice[],
  levels: SchoolLevel[],
): MoveDef => ({
  id,
  kind: 'savoir',
  name,
  why,
  writes,
  choices,
  levels,
  hora: MOVE_HORA[id] ?? 0,
  ...extras(id, 'savoir'),
});

const G = (
  id: string,
  name: string,
  why: string,
  gesture: MoveDef['gesture'],
  choices: MoveChoice[],
  levels: SchoolLevel[],
): MoveDef => ({
  id,
  kind: 'geste',
  name,
  why,
  gesture,
  choices,
  levels,
  hora: MOVE_HORA[id] ?? 0,
  ...extras(id, 'geste'),
});

/* ── Moves-savoir ─────────────────────────────────────────────────────────── */

export const MOVES: MoveDef[] = [
  S(
    'somme-angles',
    'Somme des angles d’un triangle',
    'Dans un triangle, la somme des trois angles vaut 180°.',
    'A + B + C = 180',
    [
      { text: 'A + B + C = 180', ok: true, why: 'Oui : les trois angles d’un triangle font 180°.' },
      {
        text: 'A + B + C = 360',
        why: 'C’est la somme des angles d’un quadrilatère, pas d’un triangle.',
      },
      { text: 'A + B + C = 90', why: '90°, c’est un angle droit à lui tout seul.' },
      { text: 'A + B = C', why: 'Rien ne dit que deux angles font le troisième.' },
    ],
    ['6e', '5e'],
  ),
  S(
    'perimetre-rectangle',
    'Périmètre d’un rectangle',
    'On fait le tour : deux longueurs et deux largeurs.',
    'P = (L + l) × 2',
    [
      { text: 'P = (L + l) × 2', ok: true, why: 'Oui : deux longueurs et deux largeurs.' },
      { text: 'P = L × l', why: 'Ça, c’est l’aire : le nombre de carreaux dedans.' },
      { text: 'P = L + l', why: 'Tu n’as fait que la moitié du tour.' },
    ],
    ['6e', '5e'],
  ),
  S(
    'aire-rectangle',
    'Aire d’un rectangle',
    'Longueur × largeur : le nombre de carreaux dedans.',
    'A = L × l',
    [
      { text: 'A = L × l', ok: true, why: 'Oui : longueur × largeur.' },
      { text: 'A = (L + l) × 2', why: 'Ça, c’est le périmètre : le tour.' },
      { text: 'A = L + l', why: 'On multiplie les deux côtés, on ne les ajoute pas.' },
    ],
    ['6e', '5e'],
  ),
  S(
    'aire-triangle',
    'Aire d’un triangle',
    'Base × hauteur relative à cette base, divisé par 2.',
    'A = b × h ÷ 2',
    [
      { text: 'A = b × h ÷ 2', ok: true, why: 'Oui : un triangle, c’est la moitié du rectangle.' },
      {
        text: 'A = b × h',
        why: 'C’est l’aire du rectangle entier : le triangle en est la moitié.',
      },
      { text: 'A = (b + h) ÷ 2', why: 'Ça, c’est une moyenne. Une aire multiplie deux longueurs.' },
      {
        text: 'A = b × h ÷ 3',
        why: 'La moitié, pas le tiers : un triangle remplit la moitié du rectangle.',
      },
    ],
    ['5e', '4e'],
  ),
  S(
    'aire-carre',
    'Aire d’un carré',
    'Les quatre côtés sont égaux : côté × côté.',
    'A = c × c',
    [
      { text: 'A = c × c', ok: true, why: 'Oui : côté × côté.' },
      { text: 'A = c × 4', why: 'Ça, c’est le périmètre : les quatre côtés bout à bout.' },
      { text: 'A = c + c', why: 'On multiplie les deux côtés, on ne les ajoute pas.' },
    ],
    ['6e', '5e'],
  ),
  S(
    'pythagore',
    'Théorème de Pythagore',
    'Dans un triangle rectangle, le carré de l’hypoténuse est la somme des carrés des deux autres côtés.',
    'BC² = AB² + AC²',
    [
      {
        text: 'BC² = AB² + AC²',
        ok: true,
        why: 'Oui : l’hypoténuse est seule de son côté du signe égal.',
      },
      {
        text: 'BC = AB + AC',
        why: 'Sans les carrés, ce serait le chemin par le coin — toujours plus long.',
      },
      {
        text: 'BC² = AB² − AC²',
        why: 'La soustraction sert quand on cherche un côté de l’angle droit, pas l’hypoténuse.',
      },
      { text: 'BC² = AB × AC', why: 'C’est une somme de carrés, pas un produit.' },
    ],
    ['4e', '3e'],
  ),
  S(
    'thales',
    'Théorème de Thalès',
    '(MN) ∥ (BC) : les rapports AM/AB, AN/AC et MN/BC sont égaux. Thalès s’écrit en quotients.',
    'MN ÷ BC = AM ÷ AB',
    [
      {
        text: 'MN ÷ BC = AM ÷ AB',
        ok: true,
        why: 'Oui : le petit sur le grand d’un côté, le petit sur le grand de l’autre.',
      },
      {
        text: 'MN ÷ BC = AB ÷ AM',
        why: 'Le second rapport est à l’envers : si MN/BC vaut 3/5, AM/AB vaut 3/5 aussi, pas 5/3.',
      },
      {
        text: 'MN ÷ BC = AM ÷ AC',
        why: 'AM va avec AB (même droite), pas avec AC : tu as changé de côté en route.',
      },
      {
        text: 'MN ÷ AB = AM ÷ BC',
        why: 'Tu mélanges les deux triangles : chaque rapport compare le petit et le grand d’UNE même famille.',
      },
    ],
    ['3e', '2nde'],
  ),
  S(
    'determinant',
    'Déterminant de deux vecteurs',
    'det(u⃗, v⃗) = xu × yv − xv × yu ; il vaut 0 exactement quand les vecteurs sont colinéaires.',
    'det = xu × yv − xv × yu',
    [
      {
        text: 'det = xu × yv − xv × yu',
        ok: true,
        why: 'Oui : on croise abscisse de l’un et ordonnée de l’autre.',
      },
      {
        text: 'det = xu × xv − yu × yv',
        why: 'Tu as multiplié les abscisses entre elles : rien ne se croise.',
      },
      {
        text: 'det = xu × yv + xv × yu',
        why: 'C’est une différence : avec un « + » le déterminant ne s’annulerait jamais au bon moment.',
      },
      {
        text: 'det = xu × yu − xv × yv',
        why: 'Là tu restes dans chaque vecteur au lieu de les croiser.',
      },
    ],
    ['2nde', '1re'],
  ),
  S(
    'discriminant',
    'Discriminant',
    'Pour ax² + bx + c = 0, Δ = b² − 4ac décide du nombre de solutions.',
    'Δ = b² − 4 × a × c',
    [
      {
        text: 'Δ = b² − 4 × a × c',
        ok: true,
        why: 'Oui : b au carré, moins quatre fois a fois c.',
      },
      {
        text: 'Δ = b² + 4 × a × c',
        why: 'Avec un « + », Δ serait presque toujours positif : il ne déciderait plus rien.',
      },
      { text: 'Δ = b² − 4 × c', why: 'Le coefficient a manque : il compte autant que les autres.' },
      { text: 'Δ = 2 × b − 4 × a × c', why: 'b est au CARRÉ, pas doublé.' },
    ],
    ['1re', 'tle'],
  ),
  S(
    'racines-trinome',
    'Racines du trinôme',
    'Quand Δ > 0, les deux solutions sont (−b ± √Δ) ÷ (2a).',
    'x₁ = (−b − √Δ) ÷ (2 × a)',
    [
      {
        text: 'x₁ = (−b − √Δ) ÷ (2 × a)',
        ok: true,
        why: 'Oui : −b moins racine de Δ, le tout sur 2a.',
      },
      { text: 'x₁ = (b − √Δ) ÷ (2 × a)', why: 'C’est −b, avec le signe changé.' },
      { text: 'x₁ = (−b − √Δ) ÷ 2', why: 'On divise par 2a, pas par 2.' },
      { text: 'x₁ = −b − √Δ ÷ (2 × a)', why: 'Sans parenthèses, seule la racine est divisée.' },
    ],
    ['1re', 'tle'],
  ),
  S(
    'point-fixe',
    'Point fixe d’une suite',
    'Si la suite converge vers ℓ et que f est continue, alors ℓ vérifie ℓ = f(ℓ).',
    'ℓ = 0,5 × ℓ + 3',
    [
      {
        text: 'ℓ = 0,5 × ℓ + 3',
        ok: true,
        why: 'Oui : la limite ne bouge plus, donc elle vaut son image.',
      },
      { text: 'ℓ = 0,5 × ℓ', why: 'Tu as oublié le « + 3 » de la relation de récurrence.' },
      {
        text: 'ℓ = 0,5 + 3',
        why: 'f(ℓ) n’est pas 0,5 + 3 : il faut remplacer uₙ par ℓ dans 0,5uₙ + 3.',
      },
      { text: '0 = 0,5 × ℓ + 3', why: 'La limite n’est pas nulle : elle est égale à son image.' },
    ],
    ['tle'],
  ),
  S(
    'somme-probas',
    'Somme des probabilités',
    'Les probabilités d’une loi se partagent 1 : leur somme vaut exactement 1.',
    '0,2 + 0,5 + a = 1',
    [
      {
        text: '0,2 + 0,5 + a = 1',
        ok: true,
        why: 'Oui : tout l’événement certain est partagé entre les valeurs.',
      },
      {
        text: '0,2 + 0,5 + a = 100',
        why: '1, pas 100 : les pourcentages s’écrivent 0,2 et non 20 ici.',
      },
      { text: '0,2 + 0,5 + a = 0', why: 'Une somme de probabilités n’est jamais nulle.' },
      {
        text: '0,2 × 0,5 × a = 1',
        why: 'On ajoute les probabilités des valeurs, on ne les multiplie pas.',
      },
    ],
    ['1re', 'tle'],
  ),
];

/* ── Moves-geste ──────────────────────────────────────────────────────────── */

export const GESTURE_MOVES: MoveDef[] = [
  G(
    'remplacer',
    'Remplacer',
    'Une lettre dont on connaît la valeur peut s’écrire à sa place, telle quelle.',
    'substitute',
    [
      {
        text: 'x = 5 → a + x = a + 5',
        ok: true,
        why: 'Oui : la lettre s’efface, sa valeur prend sa place.',
      },
      {
        text: 'x = 5 → a + x = 5 + x',
        why: 'Tu as écrit la valeur à la place d’une AUTRE lettre.',
      },
      { text: 'x = 5 → a + x = 5', why: 'Remplacer ne fait pas disparaître le reste de la ligne.' },
    ],
    ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'],
  ),
  G(
    'commuter',
    'Commuter',
    'Dans une somme ou un produit, l’ordre ne compte pas : a + b = b + a, a × b = b × a. On glisse un terme à une autre place (ou on le pousse avec ← →).',
    'commute',
    [
      { text: '3 + x = x + 3', ok: true, why: 'Oui : dans une somme, on peut changer l’ordre.' },
      {
        text: '5 − x = x − 5',
        why: 'La soustraction ne commute pas : 5 − 2 = 3, mais 2 − 5 = −3.',
      },
      {
        text: 'x ÷ 4 = 4 ÷ x',
        why: 'La division non plus : 8 ÷ 4 = 2, mais 4 ÷ 8 = 0,5.',
      },
    ],
    // le tout premier geste : celui de la prise en main de la feuille
    ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'],
  ),
  G(
    'distribuer',
    'Distributivité',
    'k(a + b) = ka + kb : on multiplie k par CHAQUE terme. Avec deux parenthèses, chaque terme de l’une multiplie chaque terme de l’autre.',
    'distribute',
    [
      {
        text: 'k × (a + b) = k × a + k × b',
        ok: true,
        why: 'Oui : chaque terme reçoit le facteur.',
      },
      { text: 'k × (a + b) = k × a + b', why: 'Le second terme aussi doit être multiplié par k.' },
      {
        text: 'k × (a + b) = k × a × k × b',
        why: 'C’est une somme dans la parenthèse : on distribue, on ne multiplie pas deux fois.',
      },
      {
        text: '(a + b)(c + d) = a × c + b × d',
        why: 'Il manque deux produits : a × d et b × c. Quatre produits en tout.',
      },
    ],
    ['5e', '4e', '3e', '2nde', '1re', 'tle'],
  ),
  G(
    'developper',
    'Développer',
    'Une identité du cours se développe d’un coup : (a + b)² = a² + 2ab + b², −(a − b) = −a + b.',
    'develop',
    [
      {
        text: '(a + b)² = a² + 2 × a × b + b²',
        ok: true,
        why: 'Oui : le double produit 2ab ne disparaît pas.',
      },
      { text: '(a + b)² = a² + b²', why: 'Le carré ne se distribue pas : il manque 2ab.' },
      { text: '(a + b)² = a² + a × b + b²', why: 'Le double produit, c’est 2ab, pas ab.' },
      { text: '−(a − b) = −a − b', why: 'Le « − » change le signe de CHAQUE terme : −a + b.' },
    ],
    ['4e', '3e', '2nde', '1re', 'tle'],
  ),
  G(
    'factoriser',
    'Factoriser',
    'Quand le même facteur est dans tous les termes, on le met devant la parenthèse : ka + kb = k(a + b), a² − b² = (a + b)(a − b).',
    'factor',
    [
      {
        text: 'k × a + k × b = k × (a + b)',
        ok: true,
        why: 'Oui : le facteur commun sort devant.',
      },
      {
        text: 'k × a + k × b = k × a × b',
        why: 'Dans la parenthèse il reste une SOMME, pas un produit.',
      },
      {
        text: 'k × a + k × b = 2 × k × (a + b)',
        why: 'Le facteur commun sort une fois, pas deux.',
      },
      {
        text: 'k × a − k × b = k × (a + b)',
        why: 'Le « − » reste dans la parenthèse : k(a − b).',
      },
    ],
    ['4e', '3e', '2nde', '1re', 'tle'],
  ),
  G(
    'produit-en-croix',
    'Produit en croix',
    'Deux quotients égaux se croisent : a/b = c/d donne a × d = c × b.',
    'crossmul',
    [
      {
        text: 'a ÷ b = c ÷ d → a × d = c × b',
        ok: true,
        why: 'Oui : chaque numérateur part avec le dénominateur de l’AUTRE.',
      },
      {
        text: 'a ÷ b = c ÷ d → a × b = c × d',
        why: 'Tout droit, ce n’est pas en croix : multiplier chaque fraction par son propre dénominateur ne donne rien.',
      },
      {
        text: 'a ÷ b = c ÷ d → a × c = b × d',
        why: 'Les deux numérateurs entre eux, les deux dénominateurs entre eux : l’égalité change.',
      },
      {
        text: 'a ÷ b = c ÷ d → a + d = c + b',
        why: 'Ce sont des quotients, pas des sommes : on multiplie en croix.',
      },
    ],
    // le théorème de Thalès (3e) s'écrit avec des quotients ; la 4e en a déjà besoin pour la proportionnalité
    ['4e', '3e', '2nde', '1re', 'tle'],
  ),
  G(
    'passer',
    'Passer de l’autre côté',
    'Ce qu’on retire d’un membre, on le retire de l’autre : le terme change de côté et d’opération.',
    'cross',
    [
      { text: 'x + 5 = 20 → x = 20 − 5', ok: true, why: 'Oui : on retire 5 des deux côtés.' },
      {
        text: 'x + 5 = 20 → x = 20 + 5',
        why: 'Le piège classique : en changeant de côté, « + 5 » devient « − 5 ».',
      },
      { text: '3x = 12 → x = 12 × 3', why: 'Un facteur qui passe devient une division.' },
    ],
    // dès la 6e : « C + 120 = 180 » → « C = 180 − 120 » se fait sans algèbre, en retirant 120 des deux côtés
    ['6e', '5e', '4e', '3e', '2nde', '1re', 'tle'],
  ),
];

let BY_ID = new Map([...MOVES, ...GESTURE_MOVES].map((m) => [m.id, m]));

export function moveById(id: string): MoveDef | null {
  return BY_ID.get(id) ?? null;
}

/** Le catalogue livré avec l'app : le repli tant que la base n'a pas répondu (et pour les tests). */
export const DEFAULT_MOVES: readonly MoveDef[] = [...MOVES, ...GESTURE_MOVES].map((m) => ({
  ...m,
}));

/**
 * Le catalogue vient de la BASE (`move_catalog`, migration 0752 — édité dans l'admin) : on le pose ici
 * et tout ce qui lit `MOVES`, `GESTURE_MOVES`, `moveById`, `MOVE_HORA` le voit. Les tableaux sont modifiés
 * EN PLACE : ceux qui les ont importés gardent la bonne référence.
 */
export function setMoveCatalog(defs: readonly MoveDef[]): void {
  const sorted = [...defs].sort((a, b) => a.position - b.position || a.id.localeCompare(b.id));
  MOVES.splice(0, MOVES.length, ...sorted.filter((m) => m.kind === 'savoir'));
  GESTURE_MOVES.splice(0, GESTURE_MOVES.length, ...sorted.filter((m) => m.kind === 'geste'));
  BY_ID = new Map(sorted.map((m) => [m.id, m]));
  for (const k of Object.keys(MOVE_HORA)) delete MOVE_HORA[k];
  for (const m of sorted) MOVE_HORA[m.id] = m.hora;
}

/** Revenir au catalogue livré (tests). */
export function resetMoveCatalog(): void {
  setMoveCatalog(DEFAULT_MOVES.map((m) => ({ ...m })));
}

/**
 * La place des touches réglée dans l'admin : `kind` de rangée du clavier → place. Seuls les gestes actifs
 * qui ont une place à eux comptent ; les autres gardent la place par défaut de leur famille. PUR.
 */
export function placementOverrides(): Record<string, MovePlacement> {
  const out: Record<string, MovePlacement> = {};
  for (const m of GESTURE_MOVES)
    if (m.active && m.placement && m.gesture && m.gesture !== 'commute')
      for (const k of GESTURE_KEYS[m.gesture]) out[k] = m.placement;
  return out;
}

/**
 * Un Move est disponible à un niveau dès qu'il a été rencontré à ce niveau OU avant : la trousse
 * s'enrichit, elle ne se vide pas. Un 4e peut donc avoir « Somme des angles » sous la main. PUR.
 */
export function moveAvailableAt(m: MoveDef, level: SchoolLevel): boolean {
  if (!m.active) return false;
  const at = schoolLevelRank(level);
  if (at === null) return false;
  return m.levels.some((l) => {
    const r = schoolLevelRank(l);
    return r !== null && r <= at;
  });
}

/** Une seule écriture juste par Move, et au moins trois propositions : sinon il n'y a rien à chercher. */
export function moveIssues(m: MoveDef): string[] {
  const out: string[] = [];
  if (m.choices.length < 3) out.push('moins de trois propositions');
  if (!(m.hora >= 50 && m.hora <= 1000)) out.push('HORA du déblocage entre 50 et 1 000');
  const right = m.choices.filter((c) => c.ok);
  if (right.length !== 1) out.push('il faut exactement une écriture juste');
  if (m.kind === 'savoir' && !m.writes) out.push('un Move-savoir pose une ligne');
  if (m.kind === 'geste' && !m.gesture) out.push('un Move-geste ouvre une famille de touches');
  if (right[0] && m.kind === 'savoir' && right[0].text !== m.writes)
    out.push('l’écriture juste doit être exactement la ligne posée');
  for (const c of m.choices) if (!c.why.trim()) out.push(`« ${c.text} » n’explique rien`);
  return out;
}

/**
 * Ce que la trousse propose pour la question en cours : le Move qu'il faut, parmi des voisins
 * plausibles du même niveau. On ne dit pas lequel est le bon — c'est le premier choix de l'élève.
 */
export interface MoveShelf {
  /** la question posée en haut du panneau */
  question: string;
  /** le Move qui débloque la question */
  moveId: string;
  /** les voisins montrés à côté (de vrais Moves, qu'on débloquera ailleurs) */
  alsoIds: string[];
}

/**
 * Une étagère n'a de sens que sur une question dont la FEUILLE accepte la ligne de son
 * Move-savoir : cette ligne est le `pred` de la feuille (`{label, line, hora: 3}`), que le
 * catalogue recopie. Ailleurs, la poser serait refusé par le moteur — alors on ne propose rien.
 * On compare aux espaces près : la feuille écrit « A = b × h ÷ 2 », l'auteur peut avoir resserré.
 */
export function shelfFitsSheet(shelf: MoveShelf, predLines: readonly string[]): boolean {
  const want = moveById(shelf.moveId)?.writes;
  if (!want) return false;
  const tight = (t: string) => t.replace(/\s+/g, '').toLowerCase();
  const w = tight(want);
  return predLines.some((l) => tight(l) === w);
}

/**
 * Les lignes d'outil d'une feuille : ce que le moteur accepte d'un bond. On le lui DEMANDE
 * (`sheetToolLines`) au lieu de lire `model.preds` à la main — un pred structuré (Pythagore,
 * second degré) n'a pas de `line` écrite, c'est le moteur qui la développe.
 */
export function sheetPredLines(model: unknown): string[] {
  return sheetToolLines(model);
}

/**
 * Les deux Moves d'un niveau : la formule à trouver, puis le geste qui s'applique. Elles servent à
 * la démo guidée ET au vrai player — une étagère ne vaut que sur une question dont la feuille
 * accepte la ligne de son Move-savoir (`shelfFitsSheet`), donc ailleurs il n'y a simplement pas de
 * Moves. Quand les Moves passeront en base (table `moves`), cette table-ci disparaîtra.
 */
export const MOVE_SHELVES: Partial<Record<SchoolLevel, { savoir: MoveShelf; geste: MoveShelf }>> = {
  '6e': {
    savoir: {
      question: 'Quelle connaissance te manque ?',
      moveId: 'somme-angles',
      alsoIds: ['perimetre-rectangle', 'aire-carre'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'passer', alsoIds: ['remplacer'] },
  },
  '5e': {
    savoir: {
      question: 'Quelle formule te faut-il ?',
      moveId: 'aire-triangle',
      alsoIds: ['aire-carre', 'aire-rectangle'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'remplacer', alsoIds: ['passer'] },
  },
  '4e': {
    savoir: {
      question: 'Quelle connaissance te manque ?',
      moveId: 'pythagore',
      alsoIds: ['aire-triangle', 'somme-angles'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'remplacer', alsoIds: ['passer'] },
  },
  '3e': {
    savoir: {
      question: 'Quelle connaissance te manque ?',
      moveId: 'thales',
      alsoIds: ['pythagore', 'aire-triangle'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'remplacer', alsoIds: ['developper'] },
  },
  '2nde': {
    savoir: {
      question: 'Quelle formule te faut-il ?',
      moveId: 'determinant',
      alsoIds: ['thales', 'pythagore'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'remplacer', alsoIds: ['developper'] },
  },
  '1re': {
    savoir: {
      question: 'Quelle formule te faut-il ?',
      moveId: 'discriminant',
      alsoIds: ['racines-trinome', 'determinant'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'remplacer', alsoIds: ['developper'] },
  },
  tle: {
    savoir: {
      question: 'Quelle connaissance te manque ?',
      moveId: 'point-fixe',
      alsoIds: ['discriminant', 'racines-trinome'],
    },
    geste: { question: 'Quel geste veux-tu faire ?', moveId: 'passer', alsoIds: ['remplacer'] },
  },
};

/**
 * L'étagère d'un Move-geste à un niveau : le geste cherché en vedette, les autres gestes du niveau
 * autour. Le geste est le MÊME partout (remplacer une lettre, faire passer un terme) — c'est
 * l'étagère, pas le geste, qui change d'un niveau à l'autre. PUR.
 */
function gesteShelfFor(level: string | null | undefined, moveId: string): MoveShelf | null {
  const shelves = shelvesForLevel(level);
  if (!shelves) return null;
  /*
   * ⚠️ L'étagère des gestes montre TOUS les gestes du niveau, pas seulement deux : une question peut
   * en demander plusieurs (remplacer une valeur PUIS faire passer un terme), et un geste dont les
   * touches sont à l'écran doit toujours pouvoir se prendre dans la trousse (retour de Philippe du
   * 5/11/2026).
   */
  const lvl = level as SchoolLevel;
  const others = GESTURE_MOVES.filter((m) => m.id !== moveId && moveAvailableAt(m, lvl)).map(
    (m) => m.id,
  );
  return { question: shelves.geste.question, moveId, alsoIds: others };
}

/** L'étagère du geste « Remplacer » : une lettre dont on connaît la valeur s'écrit à sa place. */
export function substituteShelfFor(level: string | null | undefined): MoveShelf | null {
  return gesteShelfFor(level, 'remplacer');
}

/**
 * L'étagère du geste « Passer de l'autre côté ». Le Move-geste de toutes les questions qui n'ont
 * aucune lettre à remplacer et qui isolent une inconnue (retour de Philippe du 4/11/2026 : sans
 * elle, la Terminale finissait avec un seul Move dans la trousse).
 */
export function crossShelfFor(level: string | null | undefined): MoveShelf | null {
  return gesteShelfFor(level, 'passer');
}

/** Les étagères d'un niveau, lu d'une chaîne quelconque (`exercises.level` n'est pas typé). */
export function shelvesForLevel(
  level: string | null | undefined,
): { savoir: MoveShelf; geste: MoveShelf } | null {
  if (!level) return null;
  return MOVE_SHELVES[level as SchoolLevel] ?? null;
}

/**
 * Les touches qu'un geste ouvre — les `kind` des rangées du clavier (`brouillon-v2/keyboard.ts`), plus
 * `commute` (les flèches ← → et le glissé d'un terme). ⚠️ « Factoriser » ouvre `factorize`, PAS `factor` :
 * `factor` est l'outil « × k » d'une fraction, qui n'est pas un Move.
 */
export const GESTURE_KEYS: Record<MoveGesture, readonly string[]> = {
  commute: ['commute'],
  substitute: ['subst'],
  distribute: ['distribute'],
  develop: ['develop'],
  factor: ['factorize'],
  cross: ['cross'],
  crossmul: ['crossmul'],
};

/**
 * Les touches des gestes qu'on n'a PAS encore débloqués : elles n'existent pas à l'écran — ni au
 * clavier, ni à côté de la ligne (retour de Philippe du 5/11/2026 : « le geste Déplacer apparaît
 * alors qu'on ne l'a pas débloqué dans la trousse »). Un geste acquis rouvre ses touches pour
 * toujours. PUR.
 */
export function lockedGestureKeys(
  unlocked: ReadonlySet<string>,
  /** seulement ces gestes (ceux que la trousse peut proposer à ce niveau) ; absent : tous */
  among?: readonly string[],
): readonly string[] {
  const out: string[] = [];
  for (const m of GESTURE_MOVES)
    if (
      m.gesture &&
      m.active &&
      !m.unlockedByDefault &&
      !unlocked.has(m.id) &&
      (!among || among.includes(m.id))
    )
      out.push(...GESTURE_KEYS[m.gesture]);
  return [...new Set(out)];
}

/**
 * Les Moves-geste que l'écriture touchée permettrait MAINTENANT : les rangées que le clavier montrerait
 * (`kinds`, avant d'en cacher aucune), et les flèches ← → (`commute`). C'est ce qui décide quel geste la
 * trousse appelle : un geste verrouillé « en jeu » est le nouveau geste à débloquer. PUR.
 */
export function gesturesInPlay(kinds: readonly string[]): string[] {
  const have = new Set(kinds);
  return GESTURE_MOVES.filter(
    (m) => m.gesture && GESTURE_KEYS[m.gesture].some((k) => have.has(k)),
  ).map((m) => m.id);
}

/**
 * L'étagère vue depuis le Move qu'on touche. Un GESTE se prend tel quel : il n'y a rien à deviner,
 * et une question peut en demander deux (remplacer une valeur PUIS faire passer un terme). Un
 * SAVOIR garde son étagère : y choisir la bonne connaissance EST l'exercice. PUR.
 */
export function shelfRootedOn(shelf: MoveShelf, id: string): MoveShelf {
  if (id === shelf.moveId || moveById(id)?.kind !== 'geste') return shelf;
  if (!shelf.alsoIds.includes(id)) return shelf;
  return {
    ...shelf,
    moveId: id,
    alsoIds: [shelf.moveId, ...shelf.alsoIds.filter((x) => x !== id)],
  };
}
